import { useMemo } from 'react';

import { useCrowdfundingProvider } from '#Context/crowdfunding/Crowdfunding.provider';
import { useCampaignShortLinks } from '#crowdfunding/pages/Management/hooks/useCampaignShortLinks.js';
import { useUserAccessPermissions, useUserData } from '@givebacks/api/core';
import {
  PeerParticipant,
  useShowCampaignPeer,
  useShowConsumerCampaignById,
} from '@givebacks/api/store';
import { BASE_URL } from 'core/Utils';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  ButtonMantine,
  Card,
  Center,
  CopyButton,
  FullWidthButtonGroup,
  Group,
  Modal,
  Stack,
  Text,
  ThemeIcon,
} from 'ui';
import { IconCircleCheck } from 'ui/Icons';

const RegistrationSuccess = ({
  participant,
  participantConvention,
}: {
  participant: PeerParticipant;
  participantConvention: string;
}) => {
  const navigate = useNavigate();

  const { causeId } = useCrowdfundingProvider();
  const { participantCenterUrl } = useCampaignShortLinks();
  const { data: permissions } = useUserAccessPermissions({
    causeId,
    accessType: 'fundraiser',
  });

  const { data: user } = useUserData();

  const hasEditAccess = useMemo(
    () => user && (user?.email === participant.email || permissions?.write),
    [user, participant.email, permissions?.write]
  );
  const isDifferentUser = useMemo(
    () => user && !permissions?.write && user?.email !== participant.email,
    [user, participant.email]
  );

  const stepOneCopy = hasEditAccess
    ? `Set up your ${participantConvention} page`
    : `Sign in to customize your ${participantConvention} page`;

  const stepTwoCopy = hasEditAccess ? `Share and collect donations` : `Share your personal link`;
  const participantLink = useMemo(
    () => `${participantCenterUrl}/participants/${participant.uuid}`,
    [participantCenterUrl, participant.uuid]
  );
  const primaryAction = useMemo(() => {
    switch (true) {
      case hasEditAccess:
        return {
          label: `Set Up ${participantConvention} Page`,
          onClick: () => navigate('../setup'),
        };
      case isDifferentUser:
        return {
          label: 'Okay',
          onClick: () => navigate('..'),
        };
      default:
        return {
          label: 'Sign In',
          onClick: () => {
            const relativeCenterUrl = participantLink.replace(BASE_URL, '');
            navigate(`/sign-in?redirect=${encodeURIComponent(relativeCenterUrl)}`);
          },
        };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasEditAccess, isDifferentUser]);

  return (
    <>
      <Stack gap="sm">
        <Text fz="lg" fw={600}>
          You are now registered to participate!
        </Text>
        {isDifferentUser && (
          <Alert type="warning">
            You registered with a different email than the one you're signed in with. Please sign
            out and log in using the email you registered with.
          </Alert>
        )}

        <Card bg="cobalt.0" radius="sm">
          <Stack>
            <Text fw={500}>Next Steps!</Text>
            <Stack gap="sm">
              <Group gap="xs" wrap="nowrap">
                <Badge variant="filled" w={20} h={20} p={0} style={{ flexShrink: 0 }}>
                  1
                </Badge>

                <Text fz="sm">{stepOneCopy}</Text>
              </Group>
              <Group gap="xs" wrap="nowrap">
                <Badge variant="filled" w={20} h={20} p={0} style={{ flexShrink: 0 }}>
                  2
                </Badge>
                <Text fz="sm">{stepTwoCopy}</Text>
              </Group>
              <Group gap="xs" wrap="nowrap">
                <Badge variant="filled" w={20} h={20} p={0} style={{ flexShrink: 0 }}>
                  3
                </Badge>
                <Text fz="sm">Access your page anytime from the Give tab in Givebacks</Text>
              </Group>
            </Stack>
          </Stack>
        </Card>

        <Text fz="xs">
          You will receive an email confirmation that will include helpful information for you to
          get started collecting donations.
        </Text>
      </Stack>
      <Modal.AffixedFooter>
        <FullWidthButtonGroup>
          <CopyButton value={participantLink}>
            {({ copied, copy }) => (
              <ButtonMantine onClick={copy} variant="outline">
                {copied ? 'Copied' : 'Copy Link to Page'}
              </ButtonMantine>
            )}
          </CopyButton>
          <ButtonMantine onClick={primaryAction.onClick} miw="50%">
            {primaryAction.label}
          </ButtonMantine>
        </FullWidthButtonGroup>
      </Modal.AffixedFooter>
    </>
  );
};
const ParticipantPostRegistrationSuccess = () => {
  const { participant_uuid } = useParams();
  const { causeId, campaignId } = useCrowdfundingProvider();
  const { data: campaign, isError: isCampaignError } = useShowConsumerCampaignById({
    causeId,
    campaignId,
  });
  const { data: participant, isError: isParticipantError } = useShowCampaignPeer({
    peerType: 'participants',
    peerId: participant_uuid,
    causeId,
    campaignId,
  });
  const navigate = useNavigate();

  if (isParticipantError || isCampaignError) {
    return <Navigate to={'..'} />;
  }

  if (!participant || !campaign) return null;
  return (
    <Modal
      opened
      onClose={() => navigate('..')}
      title={
        <Center ml={40}>
          <ThemeIcon variant="transparent" size={64} radius="xl" color="mint.7">
            <IconCircleCheck size={64} />
          </ThemeIcon>
        </Center>
      }
      styles={{ title: { width: '100%' } }}
    >
      <RegistrationSuccess
        participant={participant}
        participantConvention={campaign?.participant_options.convention_singular}
      />
    </Modal>
  );
};

export { ParticipantPostRegistrationSuccess };
